import {
  type FC,
  type Ref,
  type ReactElement,
  forwardRef,
  useMemo,
} from 'react';
import {
  Tooltip as MaterialTooltip,
  type TooltipProps as MaterialTooltipProps,
} from '@material-tailwind/react';
import { type NotNull } from '@src/types/utils';
import { classNames } from '@src/utils/classNames';

export interface TooltipProps extends MaterialTooltipProps {
  children: ReactElement;
  withArrow?: boolean;
}

const BASIC_POSITIONS = ['top', 'bottom', 'right', 'left'] as const;

export const Tooltip: FC<TooltipProps> = forwardRef(
  (
    { children, placement = 'top', withArrow = false, className, ...props },
    ref: Ref<HTMLSpanElement>,
  ) => {
    const arrowClasses = useMemo(() => {
      if (!withArrow) {
        return '';
      }
      const defaultArrowClasses =
        'before:size-4 before:rotate-45 before:absolute';
      const arrowClassesByPlacement: Partial<
        Record<NotNull<TooltipProps['placement']>, string>
      > = {
        'top': 'before:-bottom-1 before:left-0 before:right-0 before:mx-auto',
        'bottom': 'before:-top-1 before:left-0 before:right-0 before:mx-auto',
        'bottom-end': 'before:-top-1 before:mx-auto before:right-3',
        'right':
          'before:bottom-0 before:top-0 before:-left-1 before:right-0 before:my-auto',
        'left':
          'before:bottom-0 before:top-0 before:left-0 before:-right-1 before:my-auto',
      };
      if (arrowClassesByPlacement[placement]) {
        return classNames(
          arrowClassesByPlacement[placement],
          defaultArrowClasses,
        );
      }
      const position = BASIC_POSITIONS.find((pos) => placement.startsWith(pos));
      return classNames(
        position ? arrowClassesByPlacement[position] : '',
        defaultArrowClasses,
      );
    }, [placement, withArrow]);

    return (
      <MaterialTooltip
        className={classNames(
          arrowClasses,
          !props.content && 'hidden',
          'z-[var(--z-index-tooltip)]',
          className,
        )}
        placement={placement}
        {...props}
      >
        <span ref={ref} className="block">
          {children}
        </span>
      </MaterialTooltip>
    );
  },
);
